import { bucketRange, bucketStart, parseTs, type Bucket, type Window } from '../lib/time.ts'
import { columnIndex, num, type Table } from '../sdk/search.ts'

export interface AggregatedSeries {
  key: string
  label: string
  /** Per-bucket sums, aligned index-for-index with `bucketStarts`. */
  buckets: number[]
  /** Running total of `buckets`. */
  cumulative: number[]
  total: number
  /** Rows that carried a usable value for this series. */
  samples: number
  /** Rows dropped for an unparseable timestamp or value. */
  dropped: number
  /** Which fetched table supplied it — the reply is a whole record, so one
   *  fetch can answer several series. */
  sourceKey: string
}

export interface Aggregation {
  bucketStarts: number[]
  series: AggregatedSeries[]
  bucket: Bucket
}

export interface SeriesSource {
  key: string
  label: string
  /** Machine name of the column carrying the value. */
  column: string
  table: Table
  sourceKey: string
  /** Multiplier applied to every raw value (dL → L is 0.1). */
  scale: number
}

/** Sum one column of one table into aligned buckets. */
export function aggregateSeries(
  src: SeriesSource,
  bucketStarts: number[],
  bucket: Bucket,
): AggregatedSeries {
  const slot = new Map<number, number>()
  for (const b of bucketStarts) slot.set(b, 0)

  const tsIdx = columnIndex(src.table, 'ts')
  const valIdx = columnIndex(src.table, src.column)
  let samples = 0
  let dropped = 0

  if (tsIdx >= 0 && valIdx >= 0) {
    for (const row of src.table.rows) {
      const ms = parseTs(row[tsIdx])
      const v = num(row[valIdx])
      if (ms === null || v === null) {
        dropped++
        continue
      }
      const b = bucketStart(ms, bucket)
      if (!slot.has(b)) continue // outside the requested window
      slot.set(b, slot.get(b)! + v * src.scale)
      samples++
    }
  }

  const buckets = bucketStarts.map((b) => slot.get(b) ?? 0)
  const cumulative: number[] = []
  let running = 0
  for (const v of buckets) {
    running += v
    cumulative.push(running)
  }

  return {
    key: src.key,
    label: src.label,
    buckets,
    cumulative,
    total: running,
    samples,
    dropped,
    sourceKey: src.sourceKey,
  }
}

export function aggregate(
  sources: SeriesSource[],
  window: Window,
  bucket: Bucket,
): Aggregation {
  const bucketStarts = bucketRange(window.fromMs, window.toMs, bucket)
  return {
    bucketStarts,
    bucket,
    series: sources.map((s) => aggregateSeries(s, bucketStarts, bucket)),
  }
}
